import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Heart, Instagram } from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { BrandLogo } from "../components/BrandLogo";
import HeroMedia from "../components/HeroMedia";
import ProductVideoSection from "../components/ProductVideoSection";
import { ProductStack } from "../components/ProductStack";
import { useT } from "../lib/i18n";
import { api, API, money } from "../lib/api";
import { track } from "../lib/track";

const IMG = {
  s1: "/brand/sample-1.jpg", s2: "/brand/sample-2.jpg", s3: "/brand/sample-3.jpg",
  howPhotos: "/brand/how-photos.jpg", print: "/brand/love-print.jpg",
  gift: "/brand/love-gift.jpg", decor: "/brand/love-decor.jpg",
};
const SAMPLES = [IMG.s1, IMG.s2, IMG.s3];
const KIT_PRICES = [1999, 3499, 4999, 6499, 8999, 11999];

export default function Landing() {
  const { t, lang, toggle } = useT();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const kits = settings?.kits || [];
  const startingCents = kits.length ? Math.min(...kits.map((k) => Number(k.price_cents))) : Math.min(...KIT_PRICES);
  const footerIg = settings?.instagram || { show_in_footer: true, handle: "@momentsmade.co_", url: "https://instagram.com/momentsmade.co_" };
  const showAdmin = settings ? settings.show_admin_link !== false : true;
  const videoOn = !!(settings?.product_video?.enabled && settings?.product_video?.has_video);
  const gs = settings?.gift_section;

  useEffect(() => { api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  useEffect(() => { track("homepage_viewed"); }, []);
  const go = (source) => { if (source) track("cta_click", { source }); navigate(`/create${source ? `?src=${source}` : ""}`); };

  const steps = [
    { t: t("how_1_t"), d: t("how_1_d"), visual: <ProductStack images={SAMPLES} size={44} /> },
    { t: t("how_2_t"), d: t("how_2_d"), img: IMG.howPhotos },
    { t: t("how_3_t"), d: t("how_3_d"), img: IMG.print },
  ];
  const benefits = [
    { img: IMG.print, title: t("benefit_print_t"), d: t("benefit_print_d") },
    { img: IMG.gift, title: t("benefit_gift_t"), d: t("benefit_gift_d") },
    { img: IMG.s1, title: t("benefit_memories_t"), d: t("benefit_memories_d") },
    { img: IMG.decor, title: t("benefit_decor_t"), d: t("benefit_decor_d") },
  ];

  return (
    <div className="min-h-dvh">
      {/* Compact header */}
      <header className="sticky top-0 z-40 border-b bg-[hsl(var(--background))]/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-2">
          <BrandLogo size={32} withText />
          <div className="flex items-center gap-2">
            <button onClick={toggle} data-testid="lang-toggle" className="h-9 rounded-full border px-3 text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]">{lang === "en" ? "PT" : "EN"}</button>
            <Button onClick={() => go("header_cta")} variant="outline" data-testid="header-start-button" className="h-9 rounded-full border px-3.5 text-sm font-semibold">{t("create_cta")}</Button>
          </div>
        </div>
      </header>

      {/* Hero — product landing */}
      <section className="hero-wash">
        <div className="mx-auto grid max-w-6xl items-center gap-6 px-4 py-8 sm:py-12 md:grid-cols-2 md:gap-10">
          <div className="order-2 md:order-1">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-wide" style={{ color: "hsl(var(--brand-rose))" }}>
              <Heart className="h-3.5 w-3.5" /> {t("slogan")}
            </span>
            <h1 className="mt-3 font-display text-3xl font-semibold leading-[1.1] tracking-tight sm:text-[2.6rem]" style={{ color: "hsl(var(--brand-brown))" }}>
              {t("hero_title2")}
            </h1>
            <p className="mt-3 max-w-md text-base leading-relaxed text-[hsl(var(--muted-foreground))]">{t("hero_support")}</p>
            {startingCents != null && (
              <p className="mt-4 font-display text-lg font-semibold" data-testid="hero-starting-price" style={{ color: "hsl(var(--brand-rose))" }}>
                {t("starting_at", { price: money(startingCents) })}
              </p>
            )}
            <Button onClick={() => go("hero_cta")} data-testid="start-personalization-button" className="mt-5 h-12 rounded-xl px-6 text-base">
              {t("create_cta")} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
          <div className="order-1 md:order-2">
            <HeroMedia hero={settings?.hero_media} />
          </div>
        </div>
      </section>

      {/* Optional product video (between hero and How it works) */}
      <ProductVideoSection pv={settings?.product_video} />

      {/* Video CTA — only when the product video is shown */}
      {videoOn && (
        <section className="mx-auto max-w-3xl px-4 pt-6 text-center" data-testid="video-cta">
          <p className="font-display text-lg font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>{t("cta_video_q")}</p>
          <Button onClick={() => go("video_cta")} data-testid="video-cta-button" className="mt-3 h-12 rounded-xl px-6 text-base">{t("cta_want_mine")} <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
        </section>
      )}

      {/* How it works */}
      <section id="how" className="mx-auto max-w-6xl px-4 py-12">
        <h2 className="text-center text-2xl font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>{t("how_title")}</h2>
        <div className="mt-7 grid gap-5 sm:grid-cols-3">
          {steps.map((s, i) => (
            <div key={s.t} className="flex flex-col items-center rounded-[1.1rem] border bg-card p-5 text-center shadow-[var(--shadow-card)]">
              <div className="flex h-28 w-full items-center justify-center overflow-hidden rounded-xl bg-[hsl(var(--muted))]">
                {s.img ? <img src={s.img} alt="" className="h-full w-full object-cover" /> : s.visual}
              </div>
              <div className="mt-4 grid h-8 w-8 place-items-center rounded-full font-display text-sm font-semibold" style={{ background: "hsl(var(--brand-rose))", color: "white" }}>{i + 1}</div>
              <h3 className="mt-2 text-lg font-semibold">{s.t}</h3>
              <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Conversion band after How It Works — intentionally distinct from the info cards */}
      <section className="mx-auto max-w-5xl px-4 pb-12" data-testid="how-cta">
        <div
          className="relative overflow-hidden rounded-[1.5rem] px-6 py-8 shadow-[var(--shadow-soft)] sm:px-10 sm:py-10"
          style={{ background: "linear-gradient(135deg, hsl(var(--brand-blush)) 0%, hsl(var(--brand-cream)) 58%, #ffffff 100%)" }}
        >
          <div className="grid items-center gap-7 sm:grid-cols-[1.15fr_1fr]">
            <div className="order-2 sm:order-1">
              <h2 className="font-display text-2xl font-semibold leading-tight sm:text-[1.8rem]" style={{ color: "hsl(var(--brand-brown))" }}>{t("promo_headline")}</h2>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-[hsl(var(--muted-foreground))]">{t("promo_support")}</p>
              <Button onClick={() => go("how_it_works_cta")} data-testid="how-cta-button" className="mt-5 h-12 rounded-xl px-6 text-base">{t("cta_create_now")} <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
            </div>
            <div className="order-1 flex justify-center sm:order-2 sm:justify-end">
              <ProductStack images={SAMPLES} size={92} />
            </div>
          </div>
        </div>
      </section>

      {/* Editable Gift Section */}
      {gs?.enabled && (
        <section className="mx-auto max-w-5xl px-4 pb-12" data-testid="gift-section">
          <div className="grid items-center gap-6 overflow-hidden rounded-[1.3rem] border bg-card shadow-[var(--shadow-card)] md:grid-cols-2">
            <div className="aspect-[4/3] overflow-hidden bg-[hsl(var(--muted))] md:h-full">
              <img src={gs.has_image ? `${API}/media/gift-section?v=${gs.version}` : IMG.gift} alt={gs.title} className="h-full w-full object-cover" loading="lazy" />
            </div>
            <div className="p-6">
              <h2 className="font-display text-2xl font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>{gs.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-[hsl(var(--muted-foreground))]">{gs.description}</p>
              <Button onClick={() => go("gift_section_cta")} data-testid="gift-section-button" className="mt-4 h-12 rounded-xl px-6 text-base">{gs.cta_text || t("create_cta")} <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
            </div>
          </div>
        </section>
      )}

      {/* Why you'll love them — product focused */}
      <section className="mx-auto max-w-6xl px-4 pb-12">
        <h2 className="text-center text-xl font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>{t("benefits_title")}</h2>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {benefits.map((b) => (
            <div key={b.title} className="overflow-hidden rounded-[1.1rem] border bg-card shadow-[var(--shadow-card)]">
              <div className="aspect-[4/3] overflow-hidden bg-[hsl(var(--muted))]"><img src={b.img} alt={b.title} className="h-full w-full object-cover" loading="lazy" /></div>
              <div className="p-3">
                <div className="text-sm font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>{b.title}</div>
                <p className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">{b.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 pb-16">
        <h2 className="text-2xl font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>{t("faq_title")}</h2>
        <Accordion type="single" collapsible className="mt-4">
          {[1, 2, 3, 4].map((n) => (
            <AccordionItem key={n} value={`q${n}`}>
              <AccordionTrigger className="text-left text-sm font-medium">{t(`faq_q${n}`)}</AccordionTrigger>
              <AccordionContent className="text-sm text-[hsl(var(--muted-foreground))]">{t(`faq_a${n}`)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Product details (secondary specs, collapsed, FAQ style) */}
      <section className="mx-auto max-w-3xl px-4 pb-16" data-testid="product-details-section">
        <Accordion type="single" collapsible>
          <AccordionItem value="product-details">
            <AccordionTrigger className="text-left text-base font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>{t("product_details_title")}</AccordionTrigger>
            <AccordionContent>
              {(() => {
                const sizeCm = Number(settings?.magnet?.size_cm || 5);
                const inches = (sizeCm / 2.54).toFixed(1);
                const rows = [
                  [t("pd_size_label"), `${sizeCm} × ${sizeCm} cm (≈ ${inches} × ${inches} in)`],
                  [t("pd_corners_label"), t("pd_corners_v")],
                  [t("pd_finish_label"), t("pd_finish_v")],
                  [t("pd_backing_label"), t("pd_backing_v")],
                ];
                return (
                  <dl className="divide-y text-sm">
                    {rows.map(([k, val]) => (
                      <div key={k} className="flex justify-between gap-4 py-2">
                        <dt className="text-[hsl(var(--muted-foreground))]">{k}</dt>
                        <dd className="text-right font-medium text-[hsl(var(--foreground))]">{val}</dd>
                      </div>
                    ))}
                  </dl>
                );
              })()}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>

      <footer className="border-t bg-white/60">
        <div className="mx-auto max-w-6xl px-4 py-8">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            <div><BrandLogo size={34} withText /><p className="mt-2 max-w-sm text-xs text-[hsl(var(--muted-foreground))]">{t("footer_privacy")}</p></div>
            <nav className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm" data-testid="footer-legal">
              <a href="/privacy-policy" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="footer-privacy-link">Privacy Policy</a>
              <a href="/shipping-policy" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="footer-shipping-link">Shipping Policy</a>
              <a href="/terms" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="footer-terms-link">Terms &amp; Conditions</a>
              <a href="/returns-refunds" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="footer-returns-link">Returns &amp; Refunds</a>
              <a href="/contact" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="footer-contact-link">Contact Us</a>
              {footerIg?.show_in_footer && (footerIg?.url || footerIg?.handle) && (
                <a href={footerIg.url || "#"} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" className="inline-flex items-center gap-1.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"><Instagram className="h-4 w-4" /> Instagram</a>
              )}
            </nav>
          </div>
          <div className="mt-6 border-t pt-4 text-xs text-[hsl(var(--muted-foreground))]">
            <p>Moments Made — A brand of FMJ E-COMMERCE &amp; DIGITAL MARKETING LLC · Florida, USA</p>
            <p className="mt-1">© {new Date().getFullYear()} Moments Made. All rights reserved.
              {showAdmin && (<> · <a href="/admin" data-testid="footer-admin-link" className="underline underline-offset-2 hover:text-[hsl(var(--foreground))]">Admin Login</a></>)}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
